import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Agent } from '../../types/agent.types';

type AgentStatus = Agent['status'];
type AgentType = Agent['type'];

interface AgentsState {
  agents: Agent[];
  selectedAgent: Agent | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    status?: AgentStatus;
    type?: AgentType;
    search?: string;
  };
  pagination: {
    page: number;
    size: number;
    total: number;
  };
}

const initialState: AgentsState = {
  agents: [],
  selectedAgent: null,
  isLoading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    size: 10,
    total: 0,
  },
};

export const agentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    setAgents: (state, action: PayloadAction<Agent[]>) => {
      state.agents = action.payload;
    },
    addAgent: (state, action: PayloadAction<Agent>) => {
      state.agents.push(action.payload);
    },
    updateAgent: (state, action: PayloadAction<Agent>) => {
      const index = state.agents.findIndex(agent => agent.id === action.payload.id);
      if (index !== -1) {
        state.agents[index] = action.payload;
      }
    },
    removeAgent: (state, action: PayloadAction<string>) => {
      state.agents = state.agents.filter(agent => agent.id !== action.payload);
    },
    setSelectedAgent: (state, action: PayloadAction<Agent | null>) => {
      state.selectedAgent = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<AgentsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setPagination: (state, action: PayloadAction<Partial<AgentsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    updateAgentStatus: (state, action: PayloadAction<{ id: string; status: AgentStatus }>) => {
      const agent = state.agents.find(a => a.id === action.payload.id);
      if (agent) {
        agent.status = action.payload.status;
        agent.updatedAt = new Date().toISOString();
      }
    },
  },
});

export const {
  setAgents,
  addAgent,
  updateAgent,
  removeAgent,
  setSelectedAgent,
  setLoading,
  setError,
  clearError,
  setFilters,
  clearFilters,
  setPagination,
  updateAgentStatus,
} = agentsSlice.actions;

export default agentsSlice.reducer;
